<template>
    <div class="group-batch-set-path" v-if="visible">
        <div class="title-wrapper">
            <div class="title">运动设置</div>
            <div class="close-icon" @click="handleClose">
                <CloseOutlined />
            </div>
        </div>
        <div class="content-wrapper">
            <div class="mark-end">
                <p>标注运动终点</p>
                <a-button type="link" @click="handleBuildEnd">{{ endPosition ? '重新标注' : '开始标注' }}</a-button>
            </div>
            <div class="list-wrapper">
                <a-checkbox  v-model:checked="checkAll">全选</a-checkbox>
                <div class="icon-item" :class="{active:item.checked}" v-for="item in current.childItems" :key="item.id">
                    <div class="icon-item-top">
                        <a-checkbox :disabled="globalAutoSpeed || disabled" size="small"
                            v-model:checked="item.checked" @change="changeChild"></a-checkbox>
                        <img :src="item.downloadUrl" class="img" width="16" height="16" />
                        <div class="name">{{ item.iconName }}</div>
                    </div>
                    <div class="icon-item-bottom">
                        <span class="label">运动速度</span>
                        <div>
                            <a-input-number  :disabled="globalAutoSpeed || disabled"
                                style="width: 160px;" size="small" v-model:value="item.speed" :min="5" :max="150">
                                <template #addonAfter>
                                    <a-select  :disabled="globalAutoSpeed || disabled" size="small"
                                        v-model:value="item.speedUnit">
                                        <a-select-option value="m/s">m/s</a-select-option>
                                        <a-select-option value="km/h">km/h</a-select-option>
                                    </a-select>
                                </template>
                            </a-input-number>
                        </div>
                    </div>
                </div>
            </div>
            <div class="global-setting">
                <div class="speed">
                    <span>全局设置</span>

                    <a-input-number  :disabled="animationFrameId" style="width: 160px;" size="small"
                        v-model:value="globalSpeed" :min="5" :max="150">
                        <template #addonAfter>
                            <a-select  :disabled="animationFrameId" size="small"
                                v-model:value="globalUnit">
                                <a-select-option value="m/s">m/s</a-select-option>
                                <a-select-option value="km/h">km/h</a-select-option>
                            </a-select>
                        </template>
                    </a-input-number>

                </div>
            </div>
            <a-checkbox size="small"  v-model:checked="globalAutoSpeed" @change="handleChangeSpeed">
                全局应用相同速度
            </a-checkbox>
            <a-checkbox size="small"  style="margin-left: 0;" v-model:checked="current.loopMove"> 循环运动
            </a-checkbox>
        </div>
        <div class="footer-wrapper">
            <a-button size="small" type="primary" ghost @click="handleGetPath">生成路径</a-button>
            <a-button size="small" type="primary" :disabled="playStatusBtn" @click="handlePlayPath">{{ playStatus ?
                '暂停运动' :
                '开始运动'
            }}</a-button>
        </div>
    </div>
</template>
<script setup lang="ts">
import { nextTick, ref, toRefs } from 'vue'
import * as Cesium from 'cesium'
import { message } from 'ant-design-vue'
import axios from 'axios'
import { CURRENT_CONFIG } from '/@/api/http/config'
import gcoord from 'gcoord'
import { getPointPosition } from '/@/utils/kmlParse'

// eslint-disable-next-line no-undef
const props = defineProps<{
    drawTool: any;
    viewer: any;
}>()
// eslint-disable-next-line no-undef
const emits = defineEmits(['update:visible', 'updateCurrent'])
const { drawTool, viewer } = toRefs(props)

const visible = ref(false)
const current = ref<any>({})
const globalSpeed = ref(60)
const globalUnit = ref('km/h')
const globalAutoSpeed = ref(false)
const checkAll = ref(true)
const endPosition = ref('')
let endPoint: Cesium.Entity | null = null
const disabled = ref(false)
const playStatusBtn = ref(false)

// 运动相关状态
const playStatus = ref(false)
let animationFrameId: number | null = null
let isMoving = false
let paused = false
let pauseStartTime = 0
let totalPausedTime = 0
let startTime = 0

// 存储每个实体的动画数据
interface AnimationData {
    entity: Cesium.Entity;
    positions: Cesium.Cartesian3[];
    totalDistance: number;
    speedMps: number;
    loop: boolean;
    originalPosition: Cesium.Cartesian3;
    originalOrientation?: any;
    finished?: boolean; // 标记是否已完成
}

const animations = new Map<string, AnimationData>()

// 工具函数：速度单位转 m/s
const toMetersPerSecond = (value: number, unit: string): number => {
  return unit === 'km/h' ? value / 3.6 : value
}

const handleChangeSpeed = () => {
  nextTick(() => {
    if (globalAutoSpeed.value) {
      current.value.childItems.forEach((item: any) => {
        item.speed = globalSpeed.value
        item.speedUnit = globalUnit.value
      })
      disabled.value = true
    } else {
      if (!playStatus.value) {
        disabled.value = false
      }
    }
  })
}

// 停止所有运动并恢复原始位置
const stopMovement = () => {
  if (animationFrameId) {
    cancelAnimationFrame(animationFrameId)
    animationFrameId = null
  }
  isMoving = false
  paused = false
  pauseStartTime = 0
  totalPausedTime = 0

  animations.forEach((data, entityId) => {
    const entity = viewer.value.entities.getById(entityId)
    if (entity) {
      entity.position = new Cesium.ConstantPositionProperty(data.originalPosition)
      if (data.originalOrientation) {
        entity.orientation = data.originalOrientation
      } else {
        entity.orientation = undefined
      }
      viewer.value.scene.requestRender()
    }
  })
  animations.clear()
  playStatus.value = false
}

// 暂停运动
const pauseMovement = () => {
  if (!isMoving || paused) return
  paused = true
  pauseStartTime = performance.now()
  playStatus.value = false
}

// 恢复运动
const resumeMovement = () => {
  if (!isMoving || !paused) return
  paused = false
  if (pauseStartTime > 0) {
    totalPausedTime += (performance.now() - pauseStartTime) / 1000
    pauseStartTime = 0
  }
  playStatus.value = true
  if (!animationFrameId) {
    startAnimationLoop()
  }
}

// 获取路径点（高德 API 返回数据处理）
const getRoutePath = (data: any, defaultHeight: number, finalAltitude: number): number[][] => {
  const steps = data?.route?.paths?.[0]?.steps ?? []
  const result: number[][] = []

  steps.forEach((step: any, index: number) => {
    const polyline = step?.polyline
    if (!polyline) return

    const points = polyline.split(';').map(pointStr => {
      const [lng, lat] = pointStr.split(',').map(Number)
      const [wgsLng, wgsLat] = gcoord.transform([lng, lat], gcoord.GCJ02, gcoord.WGS84)
      return [wgsLng, wgsLat, defaultHeight]
    })

    if (index > 0 && points.length > 0) {
      points.shift()
    }

    result.push(...points)
  })

  if (result.length > 0) {
    result[result.length - 1][2] = finalAltitude
  }

  return result
}

// 开始运动（主入口）
const startMovement = () => {
  const selectedItems = current.value?.childItems?.filter((item: any) => item.checked) || []
  if (selectedItems.length === 0) {
    message.warning('请至少选择一个图标')
    return
  }

  for (const item of selectedItems) {
    if (!item.polyline || !item.polyline.polyline) {
      message.warning(`图标 ${item.iconName} 未生成路径线`)
      return
    }
  }

  stopMovement()

  let anyValid = false
  selectedItems.forEach(item => {
    const entity = drawTool.value.getEntityById(item.id)
    if (!entity) {
      console.warn(`未找到实体 ID: ${item.id}`)
      return
    }

    let positions: Cesium.Cartesian3[] = []
    const polylinePositions = item.polyline.polyline.positions
    if (Array.isArray(polylinePositions)) {
      positions = polylinePositions
    } else if (polylinePositions && polylinePositions.getValue) {
      positions = polylinePositions.getValue(Cesium.JulianDate.now()) || []
    }

    if (!positions || positions.length < 2) {
      message.warning(`图标 ${item.iconName} 的路径点不足`)
      return
    }

    let totalDistance = 0
    for (let i = 0; i < positions.length - 1; i++) {
      totalDistance += Cesium.Cartesian3.distance(positions[i], positions[i + 1])
    }

    let speed = item.speed
    let unit = item.speedUnit
    if (globalAutoSpeed.value) {
      speed = globalSpeed.value
      unit = globalUnit.value
    }
    const speedMps = toMetersPerSecond(speed, unit)

    const originalPos = entity.position?.getValue(Cesium.JulianDate.now()) || positions[0]

    animations.set(item.id, {
      entity,
      positions,
      totalDistance,
      speedMps,
      loop: current.value.loopMove || false,
      originalPosition: Cesium.Cartesian3.clone(originalPos),
      originalOrientation: entity.orientation ? entity.orientation.getValue(Cesium.JulianDate.now()) : undefined,
      finished: false
    })

    anyValid = true
  })

  if (!anyValid) {
    message.warning('没有可运动的有效实体')
    return
  }

  // 重置动画状态（在 animate 首次调用时设置 startTime）
  totalPausedTime = 0
  pauseStartTime = 0
  paused = false
  isMoving = true
  disabled.value = true
  playStatus.value = true
  startAnimationLoop()
}

// 动画循环
const startAnimationLoop = () => {
  const animate = (now: number) => {
    if (!isMoving) return

    if (paused) {
      animationFrameId = requestAnimationFrame(animate)
      return
    }

    // 首次调用时设置 startTime，确保与 requestAnimationFrame 的时间戳基准一致
    if (startTime === 0) {
      startTime = now
    }

    const elapsed = (now - startTime) / 1e3 - totalPausedTime

    let anyActive = false
    animations.forEach((data, entityId) => {
      if (data.finished) return

      const { entity, positions, totalDistance, speedMps } = data
      if (!entity) return
      const loop = current.value.loopMove
      let distance = elapsed * speedMps

      if (loop && totalDistance > 0) {
        distance = distance % totalDistance
        anyActive = true
      } else if (distance >= totalDistance) {
        const finalPos = positions[positions.length - 1]
        entity.position = new Cesium.ConstantPositionProperty(finalPos)
        data.finished = true
        return
      } else {
        anyActive = true
      }

      let accumulated = 0
      let segmentIndex = 0
      for (let i = 0; i < positions.length - 1; i++) {
        const segDist = Cesium.Cartesian3.distance(positions[i], positions[i + 1])
        if (accumulated + segDist >= distance) {
          segmentIndex = i
          break
        }
        accumulated += segDist
      }

      const segStart = positions[segmentIndex]
      const segEnd = positions[segmentIndex + 1]
      const segDist = Cesium.Cartesian3.distance(segStart, segEnd)
      const t = segDist === 0 ? 0 : (distance - accumulated) / segDist
      const clampedT = Math.max(0, Math.min(1, t))

      const currentPos = Cesium.Cartesian3.lerp(segStart, segEnd, clampedT, new Cesium.Cartesian3())
      entity.position = new Cesium.ConstantPositionProperty(currentPos)

      if (segmentIndex < positions.length - 1) {
        const hpr = getPointPosition(currentPos, segEnd).h + Cesium.Math.toRadians(-90)
        const headingPitchRoll = new Cesium.HeadingPitchRoll(hpr, 0, 0)
        if (entity.orientation) {
          entity.orientation = Cesium.Transforms.headingPitchRollQuaternion(currentPos, headingPitchRoll)
        }
      }
    })

    if (anyActive) {
      animationFrameId = requestAnimationFrame(animate)
    } else {
      stopMovement()
    }
  }

  animationFrameId = requestAnimationFrame(animate)
}

// 暂停/继续控制
const handlePlayPath = () => {
  if (!endPoint) {
    message.warning('请先标注运动终点')
    return
  }

  if (playStatus.value) {
    pauseMovement()
  } else {
    if (animations.size > 0) {
      resumeMovement()
    } else {
      startMovement()
    }
  }
}

// 关闭面板
const handleClose = () => {
  stopMovement()
  disabled.value = false
  playStatusBtn.value = false
  if (endPoint) {
    viewer.value.entities.remove(endPoint)
    endPoint = null
    endPosition.value = ''
  }
  current.value?.childItems?.forEach((item: any) => {
    if (item.polyline) {
      drawTool.value.removeEntity(item.polyline.id)
      item.polyline = null
    }
    const point = drawTool.value.getEntityById(item.id)
    if (point) {
      const position = Cesium.Cartesian3.fromDegrees(item.longitude, item.latitude, item.altitude || 0)
      point.position = position
      if (Number(item.type) === 2) {
        const headingPitchRoll = new Cesium.HeadingPitchRoll(
          Cesium.Math.toRadians(item.rotate),
          0,
          0
        )
        const orientation = Cesium.Transforms.headingPitchRollQuaternion(
          position,
          headingPitchRoll
        )
        point.orientation = orientation
      }
    }
  })
  visible.value = false
}

const changeChild = () => {
  nextTick(() => {
    let allChecked = true
    current.value.childItems.forEach((item: any) => {
      if (!item.checked) {
        allChecked = false
      }
    })
    checkAll.value = allChecked
  })
}

let clickHandler: Cesium.ScreenSpaceEventHandler | null = null

const handleBuildEnd = () => {
  if (clickHandler) {
    clickHandler.destroy()
    clickHandler = null
  }

  clickHandler = new Cesium.ScreenSpaceEventHandler(viewer.value.scene.canvas)
  clickHandler.setInputAction((e: { position: Cesium.Cartesian2 }) => {
    const p = viewer.value.scene.camera.pickEllipsoid(e.position, viewer.value.scene.globe.ellipsoid)
    if (!p) return
    const positions = drawTool.value.conversion(p)
    endPosition.value = `${Number(positions.longitude).toFixed(6)},${Number(positions.latitude).toFixed(6)}`
    const point = viewer.value.entities.getById('endPoint')
    if (point) {
      viewer.value.entities.remove(point)
      endPoint = null
    }
    endPoint = viewer.value.entities.add({
      id: 'endPoint',
      position: p,
      label: {
        text: '终点',
        font: '14px sans-serif',
        style: Cesium.LabelStyle.FILL_AND_OUTLINE,
        outlineWidth: 2,
        verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
        pixelOffset: new Cesium.Cartesian2(0, -30),
        show: true
      }
    })
    if (clickHandler) {
      clickHandler.destroy()
      clickHandler = null
    }
  }, Cesium.ScreenSpaceEventType.LEFT_CLICK)
}

// 生成路径（调用高德 API）
const handleGetPath = () => {
  if (!endPosition.value) return message.warning('请先标注运动终点')
  const pos = endPosition.value.split(',')
  const endPos = gcoord.transform([pos[0], pos[1]], gcoord.WGS84, gcoord.GCJ02)
  const endStr = `${endPos[0]},${endPos[1]}`
  stopMovement()

  let hasError = false
  const fns = current.value?.childItems.map(async (item: any) => {
    if (hasError) return
    if (!item.checked) return
    playStatusBtn.value = true
    const lon = item.longitude
    const lat = item.latitude
    const height = item.altitude || 0
    if (lon === undefined || lat === undefined) {
      message.warning(`模型 ${item.iconName ?? item.id} 缺少经纬度，已跳过`)
      hasError = true
      return
    }
    const startPos = gcoord.transform([lon, lat], gcoord.WGS84, gcoord.GCJ02)
    const startStr = `${startPos[0]},${startPos[1]}`
    const params = {
      origin: startStr,
      destination: endStr,
      extensions: 'base',
      output: 'JSON',
      key: CURRENT_CONFIG.amapKey
    }
    try {
      const res = await axios.get('https://restapi.amap.com/v3/direction/driving', { params })
      const pathList = getRoutePath(res.data, height, height)
      item.sportPath = {
        path: pathList,
        distance: Number(res.data.route.paths[0].distance),
        id: item.id + new Date().getTime()
      }
      if (item.polyline) {
        const entitys = drawTool.value.getEntityById(item.polyline.id)
        if (entitys) {
          drawTool.value.removeEntity(entitys.id)
        }
      }
      item.polyline = drawTool.value.setPolyline(pathList, item.sportPath.id)
    } catch (err) {
      console.error('路径规划失败', err)
      message.error('路径规划失败')
    }
    return {
      item: item
    }
  })
  Promise.all([fns]).then(() => {
    playStatusBtn.value = false
    emits('updateCurrent', current.value)
    current.value?.childItems?.forEach((item: any) => {
      if (item.polyline) {
        drawTool.value.removeEntity(item.polyline.id)
        item.polyline = null
      }
      const point = drawTool.value.getEntityById(item.id)
      if (point) {
        const position = Cesium.Cartesian3.fromDegrees(item.longitude, item.latitude, item.altitude || 0)
        point.position = position
        if (Number(item.type) === 2) {
          const headingPitchRoll = new Cesium.HeadingPitchRoll(
            Cesium.Math.toRadians(item.rotate),
            0,
            0
          )
          const orientation = Cesium.Transforms.headingPitchRollQuaternion(
            position,
            headingPitchRoll
          )
          point.orientation = orientation
        }
      }
    })
  })
}

// 初始化数据（外部调用）
const initData = (row: any) => {
  checkAll.value = true
  globalUnit.value = 'km/h'
  if (row.childItems) {
    row.childItems.forEach((item: any) => {
      item.speed = item.speed || globalSpeed.value
      item.speedUnit = item.speedUnit || 'km/h'
      item.checked = item.checked !== undefined ? item.checked : true
    })
  }
  visible.value = true
  current.value = row
}

// 暴露方法给父组件
// eslint-disable-next-line no-undef
defineExpose({
  initData
})
</script>
<style lang="scss" scoped>
.group-batch-set-path {
    width: 320px;
    position: fixed;
    top: 80px;
    left: 340px;
    z-index: 301;
    background-color: #fff;
    border-radius: var(--borderRadius-px);

    .title-wrapper {
        height: 48px;
        padding: 0 16px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        border-bottom: 1px solid #cccccc;

        .title {
            font-size: 16px;
            font-weight: 500;
            color: #333
        }

        .close-icon {
            cursor: pointer;
            color: #333;
        }
    }

    .content-wrapper {
        min-height: 100px;
        padding: 0 20px;
        display: flex;
        flex-direction: column;

        .mark-end {
            width: 100%;
            height: 56px;
            display: inline-flex;
            justify-content: space-between;
            align-items: center;
            color: #333;
            border-bottom: 1px solid #cccccc;

            .ant-btn-link {
                padding: 0
            }
        }

        .list-wrapper {
            max-height: 240px;
            overflow-y: auto;
            margin: 8px 0;
            display: flex;
            flex-direction: column;
            gap: 8px;

            &::-webkit-scrollbar {
                width: 6px;
                height: 6px
            }

            &::-webkit-scrollbar-track {
                border-radius: 6px;
                background: rgb(var(--colorTextBase-rgb)/.1)
            }

            &::-webkit-scrollbar-thumb {
                border-radius: 6px;
                background: rgb(var(--colorTextBase-rgb)/.3)
            }

            &::-webkit-scrollbar-corner {
                background: rgb(var(--colorTextBase-rgb)/.1)
            }

            .icon-item {
                padding: 8px;
                border: 1px solid #cccccc;
                border-radius: var(--borderRadius-px);
                background-color: var(--colorFillQuaternary);
                display: flex;
                flex-direction: column;
                gap: 8px;
                &.active {
                    border-color: var(--colorPrimary)
                }
            }

            .icon-item-top {
                height: 24px;
                display: inline-flex;
                align-items: center;
                gap: 8px;

                .img {
                    object-fit: contain;
                }

                .name {
                    flex-basis: fit-content;
                    line-height: 24px;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    color: #333;
                }
            }

            .icon-item-bottom {
                height: 36px;
                display: inline-flex;
                justify-content: space-between;
                align-items: center;

                .label {
                    color: #333;
                }

                .speed-disabled {
                    opacity: .5;
                    pointer-events: none;
                    cursor: not-allowed;
                }
            }
        }

        .global-setting {
            display: flex;
            flex-direction: column;
            border-top: 1px solid #cccccc;
            gap: 8px;

            .speed {
                width: 100%;
                height: 36px;
                margin-top: 16px;
                margin-bottom: 8px;
                color: #333;
                display: inline-flex;
                justify-content: space-between;
                align-items: center;
            }
        }
    }

    .footer-wrapper {
        width: 100%;
        padding: 0 20px;
        height: 64px;
        display: inline-flex;
        justify-content: center;
        align-items: center;
        gap: 16px;
    }
}
</style>
